(function () {
  /**
   * @ngdoc component
   * @name abxQrCodeCard
   *
   * @param {Object} model - The model representing a room or asset.
   * @param {Boolean} [readonly] - Controls readonly state of input values.
   *
   * @description
   * A card that displays a QR code associated with a room or asset.
   */
  angular
    .module("akitabox.mobile.components.qrCodeCard", [
      "akitabox.core.services.qrcode",
      "akitabox.core.services.shadow",
      "akitabox.core.toast",
      "akitabox.ui.dialogs.qrScanner",
      "ui.mask",
    ])
    .controller("QrCodeCard", QrCodeCardController)
    .component("abxQrCodeCard", {
      bindings: {
        model: "<abxModel",
        readonly: "<?abxReadonly",
      },
      controller: QrCodeCardController,
      controllerAs: "vm",
      templateUrl:
        "app/mobile/components/qr-code-card/qr-code-card.component.html",
    });

  /* @ngInject */
  function QrCodeCardController(
    $q,
    QRCodeService,
    QrScannerDialog,
    ShadowService,
    ToastService
  ) {
    var self = this;

    // Private Constants
    var ERROR_ALREADY_ASSOCIATED =
      "This QR code is already associated to another room or asset.";
    var ERROR_FAILED = "Failed to save.";

    // Attributes
    /** If true, the save button will be disabled */
    self.disableSave;
    /** If true, the scan button will be disabled */
    self.disableScan;
    /** Flag controlling the editing state of the card */
    self.editing;
    /** The error message to be displayed. Falsy means no error. */
    self.errorMessage;
    /** ngModel for the qr code input. */
    self.qrCode;

    // Functions
    self.getModelQrCode = getModelQrCode;
    self.handleCancelButtonClick = handleCancelButtonClick;
    self.handleInputChange = handleInputChange;
    self.handleSaveButtonClick = handleSaveButtonClick;
    self.handleScanButtonClick = handleScanButtonClick;

    // =================
    // Lifecycle
    // =================

    /**
     * Initialize controller attributes.
     */
    self.$onInit = function () {
      self.disableSave = true;
      self.disableScan = false;
      self.errorMessage = "";
      self.qrCode = getModelQrCode();
      self.editing = false;
    };

    self.$onChanges = function (changes) {
      if (changes.model) {
        self.$onInit();
      }
    };

    // =================
    // Public Functions
    // =================

    /**
     * Get the code for the model's currently associated qr code if present.
     * @return {String} The current model's qr code or undefined if not present.
     */
    function getModelQrCode() {
      if (self.model && self.model.qrCode) {
        return self.model.qrCode._code;
      }
    }

    /**
     * Respond to user input events, keeping control up to date with the
     * current text.
     */
    function handleInputChange() {
      if (self.qrCode !== undefined) {
        self.disableSave = false;
      } else {
        self.disableSave = true;
      }
      setError();
    }

    /**
     * Respond to "cancel" button clicks. Synchronizes the input model with
     * the qrCode so that partial entries are not preserved.
     */
    function handleCancelButtonClick() {
      self.$onInit();
    }

    /**
     * Respond to "save" button clicks. Attempts to associate the provided
     * code with the current model.
     */
    function handleSaveButtonClick() {
      var didError = false;
      var qrCode = normalizeQrCode(self.qrCode);
      self.disableSave = true;
      self.disableScan = true;
      var promise = $q.resolve();
      if (self.model.qrCode) {
        var existingQrCodeId = self.model.qrCode._id;
        promise = QRCodeService.disassociate(existingQrCodeId);
      }
      return promise
        .catch(function (err) {
          ToastService.showError(err);
          return $q.reject();
        })
        .then(function () {
          delete self.model.qrCode;
          if (!qrCode) {
            ShadowService.sendEvent("qr-code", "save-qr-id", "");
            return undefined;
          }
          var associationKey = self.model.pinType.is_asset ? "asset" : "room";
          var association = {};
          association[associationKey] = self.model._id;
          return QRCodeService.associate(qrCode, association);
        })
        .then(function (result) {
          if (result === undefined) {
            delete self.model.qrCode;
          } else {
            self.model.qrCode = result;
            ShadowService.sendEvent("qr-code", "save-qr-id", qrCode.code);
          }
        })
        .catch(function (err) {
          didError = true;
          if (!err) {
            return;
          }
          if (err.status === 400) {
            setError(ERROR_ALREADY_ASSOCIATED);
          } else if (err.status === 404) {
            setError(ERROR_FAILED);
          } else {
            ToastService.showError(err);
          }
        })
        .finally(function () {
          if (didError) {
            // stay in the editing state
            return $q.reject();
          }
          self.disableSave = false;
          self.disableScan = false;
        });
    }

    /**
     * Respond to user clicking the scan button. Displays the QR scanner dialog.
     */
    function handleScanButtonClick() {
      QrScannerDialog.show({
        redirect: false,
        returnModel: true,
      }).then(function (qrCode) {
        self.qrCode = qrCode._code;
        self.handleInputChange();
      });
    }

    // =================
    // Private Functions
    // =================

    /**
     * Convert a user-provided QR code into a valid qr code value.
     * @param {String} qrCode - The user input to attempt to normalize
     * @return {String} qrCode with its dashes stripped.
     */
    function normalizeQrCode(qrCode) {
      if (!qrCode) {
        return "";
      }
      return qrCode.replace(/-/g, "");
    }

    /**
     * Display the provided error message to the user.
     * @param {String} [errorMessage] - The error message to display. The error
     *    is cleared if errorMessage is falsy or omitted.
     */
    function setError(errorMessage) {
      self.errorMessage = errorMessage;
    }
  }
})();
